import type { AuditEventKind } from '@/api/types';

export interface AuditKindMeta {
  kind: AuditEventKind;
  label: string;
  /** Tailwind text color class for the kind chip text. */
  textClass: string;
  /** Tailwind background class for the kind chip background. */
  bgClass: string;
  /** Tailwind background class for the leading dot. */
  dotClass: string;
}

export const AUDIT_KIND_META: Record<AuditEventKind, AuditKindMeta> = {
  login_succeeded: {
    kind: 'login_succeeded',
    label: '登录成功',
    textClass: 'text-[#006644]',
    bgClass: 'bg-[#E3FCEF]',
    dotClass: 'bg-[#006644]',
  },
  login_failed: {
    kind: 'login_failed',
    label: '登录失败',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  logout: {
    kind: 'logout',
    label: '登出',
    textClass: 'text-on-surface-variant',
    bgClass: 'bg-surface-container',
    dotClass: 'bg-on-surface-variant',
  },
  entry_access_denied: {
    kind: 'entry_access_denied',
    label: '入口拒绝',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  policy_changed: {
    kind: 'policy_changed',
    label: '策略变更',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  application_config_changed: {
    kind: 'application_config_changed',
    label: '应用配置变更',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  oidc_token_issued: {
    kind: 'oidc_token_issued',
    label: 'OIDC 颁发 Token',
    textClass: 'text-tertiary',
    bgClass: 'bg-tertiary-fixed',
    dotClass: 'bg-tertiary',
  },
  manifest_applied: {
    kind: 'manifest_applied',
    label: 'Manifest 已应用',
    textClass: 'text-[#006644]',
    bgClass: 'bg-[#E3FCEF]',
    dotClass: 'bg-[#006644]',
  },
  'oauth_client.create': {
    kind: 'oauth_client.create',
    label: 'OAuth Client 创建',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  'oauth_client.update': {
    kind: 'oauth_client.update',
    label: 'OAuth Client 更新',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  'oauth_client.rotate_secret': {
    kind: 'oauth_client.rotate_secret',
    label: 'OAuth Client 密钥轮换',
    textClass: 'text-tertiary',
    bgClass: 'bg-tertiary-fixed',
    dotClass: 'bg-tertiary',
  },
  'oauth_client.revoke': {
    kind: 'oauth_client.revoke',
    label: 'OAuth Client 撤销',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  'oauth_client.ownership_mismatch_skipped': {
    kind: 'oauth_client.ownership_mismatch_skipped',
    label: 'OAuth Client 归属不匹配跳过',
    textClass: 'text-on-surface-variant',
    bgClass: 'bg-surface-container',
    dotClass: 'bg-on-surface-variant',
  },
  'manifest_apply.members_parsed_but_ignored': {
    kind: 'manifest_apply.members_parsed_but_ignored',
    label: 'Members 字段被忽略',
    textClass: 'text-on-surface-variant',
    bgClass: 'bg-surface-container',
    dotClass: 'bg-on-surface-variant',
  },
  'manifest_apply.bindings_deferred_no_tenant': {
    kind: 'manifest_apply.bindings_deferred_no_tenant',
    label: '绑定因无租户延后',
    textClass: 'text-tertiary',
    bgClass: 'bg-tertiary-fixed',
    dotClass: 'bg-tertiary',
  },
  oidc_authorize_granted: {
    kind: 'oidc_authorize_granted',
    label: 'OIDC 授权放行',
    textClass: 'text-[#006644]',
    bgClass: 'bg-[#E3FCEF]',
    dotClass: 'bg-[#006644]',
  },
  oidc_authorize_denied: {
    kind: 'oidc_authorize_denied',
    label: 'OIDC 授权拒绝',
    textClass: 'text-tertiary',
    bgClass: 'bg-tertiary-fixed',
    dotClass: 'bg-tertiary',
  },
  admin_role_gate_disabled: {
    kind: 'admin_role_gate_disabled',
    label: 'Role Gate 已禁用',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  admin_role_gate_enabled: {
    kind: 'admin_role_gate_enabled',
    label: 'Role Gate 已启用',
    textClass: 'text-[#006644]',
    bgClass: 'bg-[#E3FCEF]',
    dotClass: 'bg-[#006644]',
  },
  'external_account_link.created': {
    kind: 'external_account_link.created',
    label: '外部账户链接创建',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  'external_account_link.updated': {
    kind: 'external_account_link.updated',
    label: '外部账户链接更新',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  'external_account_link.suspended': {
    kind: 'external_account_link.suspended',
    label: '外部账户链接暂停',
    textClass: 'text-tertiary',
    bgClass: 'bg-tertiary-fixed',
    dotClass: 'bg-tertiary',
  },
  'external_account_link.revoked': {
    kind: 'external_account_link.revoked',
    label: '外部账户链接撤销',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  'external_account_link.import_previewed': {
    kind: 'external_account_link.import_previewed',
    label: '外部账户导入预览',
    textClass: 'text-on-surface-variant',
    bgClass: 'bg-surface-container',
    dotClass: 'bg-on-surface-variant',
  },
  session_revoked_by_disable: {
    kind: 'session_revoked_by_disable',
    label: '禁用撤销会话',
    textClass: 'text-on-error-container',
    bgClass: 'bg-error-container',
    dotClass: 'bg-error',
  },
  sync_run_completed: {
    kind: 'sync_run_completed',
    label: '同步完成',
    textClass: 'text-[#006644]',
    bgClass: 'bg-[#E3FCEF]',
    dotClass: 'bg-[#006644]',
  },
  admin_feishu_event_view: {
    kind: 'admin_feishu_event_view',
    label: 'Feishu 事件查看',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
  admin_feishu_event_retry: {
    kind: 'admin_feishu_event_retry',
    label: 'Feishu 事件重试',
    textClass: 'text-primary',
    bgClass: 'bg-surface-container-high',
    dotClass: 'bg-primary',
  },
};

export function getAuditKindMeta(kind: string): AuditKindMeta {
  return AUDIT_KIND_META[kind as AuditEventKind] ?? {
    kind: kind as AuditEventKind,
    label: '未知审计事件',
    textClass: 'text-on-surface-variant',
    bgClass: 'bg-surface-container',
    dotClass: 'bg-on-surface-variant',
  };
}

export const AUDIT_KIND_OPTIONS: AuditEventKind[] = [
  'login_succeeded',
  'login_failed',
  'logout',
  'entry_access_denied',
  'policy_changed',
  'application_config_changed',
  'oidc_token_issued',
  'manifest_applied',
  'oauth_client.create',
  'oauth_client.update',
  'oauth_client.rotate_secret',
  'oauth_client.revoke',
  'oauth_client.ownership_mismatch_skipped',
  'manifest_apply.members_parsed_but_ignored',
  'manifest_apply.bindings_deferred_no_tenant',
  'oidc_authorize_granted',
  'oidc_authorize_denied',
  'admin_role_gate_disabled',
  'admin_role_gate_enabled',
  'external_account_link.created',
  'external_account_link.updated',
  'external_account_link.suspended',
  'external_account_link.revoked',
  'external_account_link.import_previewed',
  'session_revoked_by_disable',
  'sync_run_completed',
  'admin_feishu_event_view',
  'admin_feishu_event_retry',
];
